import { useState, useEffect } from 'react';
import axios from 'axios';
import { API, toast } from '@/App';
import { Button } from '@/components/ui/button';
import { Target, TrendingUp } from 'lucide-react';

export default function ScoreGoalWidget() {
  const [goalData, setGoalData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [inputScore, setInputScore] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchGoal(); }, []);

  const fetchGoal = async () => {
    try {
      const res = await axios.get(`${API}/score-goal`);
      setGoalData(res.data);
      if (res.data.target_score) setInputScore(String(res.data.target_score));
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  const handleSave = async () => {
    const score = parseInt(inputScore);
    if (!score || score < 400 || score > 1600) {
      toast.error('Enter a score between 400 and 1600');
      return;
    }
    try {
      await axios.post(`${API}/score-goal`, { target_score: score });
      toast.success('Goal set!');
      setEditing(false);
      fetchGoal();
    } catch (e) {
      toast.error('Failed to set goal');
    }
  };

  if (loading) return null;

  const { target_score, latest_score, gap, scores } = goalData || {};

  // Progress chart (simple bar visualization)
  const maxScore = 1600;
  const latestPct = latest_score ? (latest_score / maxScore) * 100 : 0;
  const goalPct = target_score ? (target_score / maxScore) * 100 : 0;

  return (
    <div data-testid="score-goal-widget" style={{ background: '#fff', borderRadius: 12, padding: '1.25rem', border: '1px solid #e5e7eb' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Target size={20} style={{ color: '#6366f1' }} />
          <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#1a202c', margin: 0 }}>Score Goal</h3>
        </div>
        {target_score && !editing && (
          <Button variant="ghost" onClick={() => setEditing(true)} style={{ fontSize: '0.8rem', padding: '0.25rem 0.5rem', color: '#6366f1' }}>
            Edit
          </Button>
        )}
      </div>

      {!target_score && !editing ? (
        <div style={{ textAlign: 'center', padding: '0.5rem 0' }}>
          <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.75rem' }}>Set a target SAT score to track your progress</p>
          <Button data-testid="set-goal-btn" onClick={() => setEditing(true)} style={{ background: '#6366f1', color: '#fff', fontSize: '0.85rem' }}>
            Set My Goal
          </Button>
        </div>
      ) : editing ? (
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input
            data-testid="goal-input"
            type="number"
            min="400"
            max="1600"
            step="10"
            value={inputScore}
            onChange={e => setInputScore(e.target.value)}
            placeholder="e.g. 1400"
            style={{ padding: '0.5rem 0.75rem', borderRadius: 6, border: '1px solid #d1d5db', fontSize: '0.9rem', width: 120 }}
          />
          <Button data-testid="save-goal-btn" onClick={handleSave} style={{ background: '#6366f1', color: '#fff', fontSize: '0.85rem' }}>Save</Button>
          <Button variant="ghost" onClick={() => setEditing(false)} style={{ fontSize: '0.85rem' }}>Cancel</Button>
        </div>
      ) : (
        <div>
          {/* Goal Progress Bar */}
          <div style={{ position: 'relative', height: 28, background: '#f3f4f6', borderRadius: 14, marginBottom: '0.75rem', overflow: 'visible' }}>
            {/* Latest score bar */}
            {latest_score && (
              <div style={{
                position: 'absolute', left: 0, top: 0, height: '100%',
                width: `${latestPct}%`, background: latestPct >= goalPct ? '#22c55e' : '#6366f1',
                borderRadius: 14, transition: 'width 0.5s ease',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8
              }}>
                <span style={{ color: '#fff', fontSize: '0.75rem', fontWeight: 700 }}>{latest_score}</span>
              </div>
            )}
            {/* Goal marker */}
            <div style={{
              position: 'absolute', left: `${goalPct}%`, top: -4, height: 36,
              width: 3, background: '#f59e0b', borderRadius: 2, transform: 'translateX(-50%)'
            }} />
            <div style={{
              position: 'absolute', left: `${goalPct}%`, top: -20,
              transform: 'translateX(-50%)', fontSize: '0.7rem', fontWeight: 700, color: '#f59e0b'
            }}>
              {target_score}
            </div>
          </div>

          {/* Gap message */}
          {gap !== null && gap !== undefined && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
              <TrendingUp size={16} style={{ color: gap <= 0 ? '#22c55e' : '#6366f1' }} />
              <span style={{ fontSize: '0.9rem', color: gap <= 0 ? '#22c55e' : '#374151', fontWeight: 600 }}>
                {gap <= 0 ? "You've reached your goal!" : `${gap} points to go`}
              </span>
            </div>
          )}

          {/* Score history mini-chart */}
          {scores && scores.length > 1 && (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 40, marginTop: '0.5rem' }}>
              {scores.slice(-8).map((s, i) => {
                const pct = ((s.score - 400) / 1200) * 100;
                return (
                  <div
                    key={`score-${s.date}-${i}`}
                    title={`${s.score} (${new Date(s.date).toLocaleDateString()})`}
                    style={{
                      flex: 1, height: `${Math.max(pct, 5)}%`,
                      background: s.score >= (target_score || 0) ? '#22c55e' : '#6366f1',
                      borderRadius: '3px 3px 0 0', minWidth: 8, maxWidth: 30, opacity: 0.7 + (i / scores.length) * 0.3
                    }}
                  />
                );
              })}
            </div>
          )}

          {!latest_score && (
            <p style={{ color: '#9ca3af', fontSize: '0.85rem', margin: 0 }}>Complete a test to start tracking progress</p>
          )}
        </div>
      )}
    </div>
  );
}
